import React, { useState, useEffect } from "react";
import {
  Loader,
  AlertCircle,
  FileText,
  ArrowLeft,
  X,
} from "lucide-react";
import TemplatePreview from "./templateCreator/TemplatePreview";
import { transformDuplicateTemplateToCreatorState } from "../utils/templateDataTransformers";
import { getTemplates } from "../apis/template";
import { getCampaignById, updateCampaign } from "../apis/campaign";
import { toast } from "react-toastify";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import {
  generateCSVHeaders as sharedGenerateCSVHeaders,
  validateTemplateCompatibility as sharedValidateTemplateCompatibility,
} from "../utils/campaignTemplateUtils";

const EditCampaignForm = ({ campaignId, onCancel, onSuccess }) => {
  const { selectedPhoneAccount } = usePhoneAccount();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [selectedTemplates, setSelectedTemplates] = useState([]); // Array of selected template IDs
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null); // For preview display
  const [originalTemplate, setOriginalTemplate] = useState(null); // Store original template for parameter extraction
  const [templateValidationError, setTemplateValidationError] = useState("");

  const [campaignData, setCampaignData] = useState({
    name: "",
    description: "",
    templateIds: [], // Array of template IDs
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (campaignId) {
      loadCampaign();
    }
  }, [campaignId]);

  useEffect(() => {
    if (selectedPhoneAccount?.wabaId) {
      loadTemplates(selectedPhoneAccount.wabaId);
    }
  }, [selectedPhoneAccount]);

  const loadCampaign = async () => {
    try {
      setLoadingCampaign(true);
      const response = await getCampaignById(campaignId);
      const campaign = response.data.data || response.data;

      // Parse template_ids (could be JSON string or array)
      let templateIds = [];
      if (campaign.template_ids) {
        if (typeof campaign.template_ids === 'string') {
          try {
            templateIds = JSON.parse(campaign.template_ids);
          } catch (e) {
            console.error("Error parsing template_ids:", e);
            templateIds = [];
          }
        } else if (Array.isArray(campaign.template_ids)) {
          templateIds = campaign.template_ids.map(id => String(id));
        }
      }

      setCampaignData({
        name: campaign.campaign_name || "",
        description: campaign.description || "",
        templateIds: templateIds,
      });

      setSelectedTemplates(templateIds);

      // Load templates and validate
      if (selectedPhoneAccount?.wabaId) {
        await loadTemplates(selectedPhoneAccount.wabaId);
      }
    } catch (error) {
      console.error("Error loading campaign:", error);
      toast.error("Failed to load campaign data");
      onCancel();
    } finally {
      setLoadingCampaign(false);
    }
  };

  const loadTemplates = async (wabaId) => {
    if (!wabaId) return;
    try {
      setLoading(true);
      const response = await getTemplates({ wabaId });

      // API returns metaTemplates, not templates
      const rawTemplates = response.data.metaTemplates || response.data.templates || [];
      const filtered = rawTemplates.filter(
        (t) =>
          (t.waba_id === wabaId || t.wabaId === wabaId) &&
          (t.status === "approved" || t.status === "APPROVED") &&
          (t.category || "").toLowerCase() !== "authentication"
      );
      setTemplates(filtered);

      // After templates are loaded, validate and set preview
      if (selectedTemplates.length > 0) {
        const selectedTemplateObjects = selectedTemplates
          .map(id => filtered.find(t => String(t.id).trim() === String(id).trim()))
          .filter(Boolean);

        if (selectedTemplateObjects.length > 0) {
          const validation = validateTemplateCompatibility(selectedTemplateObjects);
          setTemplateValidationError(validation.error);

          if (validation.valid) {
            const firstTemplate = selectedTemplateObjects[0];
            const transformedTemplate = transformTemplateForPreview(firstTemplate);
            setSelectedTemplateForPreview(transformedTemplate);
            setOriginalTemplate(firstTemplate);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load templates:", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCampaignData((prev) => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Transform template from API format to preview format
  const transformTemplateForPreview = (template) => {
    if (!template) return null;
    
    if (template.body && !template.template_json) {
      return {
        ...template,
        headerFormat: template.headerFormat || template.headerType || "NONE",
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        console.error("Error parsing template_json:", e);
        templateJson = {};
      }
    }
    
    const components = templateJson.components || [];
    
    if (components.length === 0) {
      return {
        ...template,
        body: template.body || '',
        footer: template.footer || '',
        headerFormat: template.headerFormat || template.headerType || "NONE",
        headerText: template.headerText || '',
        header_media_url: template.header_media_url || null,
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    const bodyComponent = components.find(c => c.type === 'BODY');
    const body = bodyComponent?.text || template.body || '';
    
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footer = footerComponent?.text || template.footer || '';
    
    const headerComponent = components.find(c => c.type === 'HEADER');
    const headerFormat = headerComponent?.format?.toUpperCase() || template.headerFormat || template.headerType?.toUpperCase() || 'NONE';
    const headerText = headerComponent?.text || template.headerText || '';
    const headerMediaUrl = template.header_media_url || null;
    
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const rawButtons = buttonsComponent?.buttons || template.buttons || [];
    const buttons = rawButtons.map((btn, index) => ({
      id: index,
      type: btn.type,
      text: btn.text || btn.url || btn.phone_number || `Button ${index + 1}`,
      url: btn.url,
      phone_number: btn.phone_number,
    }));
    
    const variableMatches = body.match(/\{\{(\d+)\}\}/g) || [];
    const variables = variableMatches.map((match, index) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      return {
        id: index,
        name: varNum,
        sample: bodyComponent?.example?.body_text?.[index] || `Sample ${varNum}`,
      };
    });
    
    return {
      ...template,
      body,
      footer,
      headerFormat,
      headerText,
      header_media_url: headerMediaUrl,
      buttons,
      variables,
    };
  };

  const validateTemplateCompatibility = (selectedTemplateObjects) => {
    return sharedValidateTemplateCompatibility(selectedTemplateObjects);
  };

  const generateCSVHeaders = (template) => sharedGenerateCSVHeaders(template);

  /**
   * Handle template selection (checkbox toggle)
   */
  const handleTemplateToggle = (templateId) => {
    const normalizedTemplateId = String(templateId).trim();
    const currentSelected = [...selectedTemplates];
    
    const index = currentSelected.findIndex(id => String(id).trim() === normalizedTemplateId);
    
    let newSelected;
    if (index >= 0) {
      newSelected = currentSelected.filter(id => String(id).trim() !== normalizedTemplateId);
    } else {
      newSelected = [...currentSelected, normalizedTemplateId];
    }

    setSelectedTemplates(newSelected);
    handleInputChange("templateIds", newSelected);

    // Validate template compatibility
    if (newSelected.length > 0) {
      const selectedTemplateObjects = newSelected
        .map(id => templates.find(t => String(t.id).trim() === String(id).trim()))
        .filter(Boolean);

      const validation = validateTemplateCompatibility(selectedTemplateObjects);
      setTemplateValidationError(validation.error);

      if (validation.valid && selectedTemplateObjects.length > 0) {
        const firstTemplate = selectedTemplateObjects[0];
        const transformedTemplate = transformTemplateForPreview(firstTemplate);
        setSelectedTemplateForPreview(transformedTemplate);
        setOriginalTemplate(firstTemplate);
      } else {
        setSelectedTemplateForPreview(null);
        setOriginalTemplate(null);
      }
    } else {
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setTemplateValidationError("");
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!campaignData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }
    if (!campaignData.templateIds || campaignData.templateIds.length === 0) {
      newErrors.templateIds = "Please select at least one template";
    }
    if (templateValidationError) {
      newErrors.templateIds = templateValidationError;
    }
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const updateData = {
          campaign_name: campaignData.name,
          description: campaignData.description || "",
          template_ids: campaignData.templateIds.map(id => parseInt(id)).filter(id => !isNaN(id)),
        };
        
        await updateCampaign(campaignId, updateData);
        toast.success("Campaign updated successfully");
        onSuccess?.();
        onCancel();
      } catch (error) {
        console.error("Error updating campaign:", error);
        const errorMessage = error.response?.data?.error || error.message || "Failed to update campaign";
        toast.error(errorMessage);
        if (error.response?.data?.error) {
          setErrors((prev) => ({ ...prev, templateIds: error.response.data.error }));
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (loadingCampaign) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-4 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-[#25D366]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go back"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Edit Campaign</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-3 py-1.5 text-xs font-medium text-white bg-[#25D366] rounded-lg hover:bg-[#20BD5A] flex items-center gap-1.5 ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting && <Loader className="w-3 h-3 animate-spin" />}
              {isSubmitting ? "Updating..." : "Update Campaign"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-3 lg:max-h-[calc(100vh-120px)] lg:overflow-y-auto lg:pr-2">
            {/* Campaign Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Campaign Information
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Welcome Campaign"
                    className={`w-full px-2.5 py-1.5 text-xs border ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]`}
                  />
                  {errors.name && (
                    <p className="text-[10px] text-red-600 mt-0.5">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={campaignData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe your campaign..."
                    rows={2}
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366] resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Template Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Select Templates * (Multiple Selection)
              </h2>
              <p className="text-[10px] text-gray-600 mb-3">
                Select multiple templates to shuffle and send. All templates must have the same parameter requirements (same number of header and body parameters).
              </p>
              {!selectedPhoneAccount ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    Please select a WhatsApp Business Account from the header
                  </p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader className="w-5 h-5 animate-spin text-[#25D366]" />
                </div>
              ) : (
                <div>
                  <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-2">
                    {templates.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-4">
                        No approved templates found for this WABA account
                      </p>
                    ) : (
                      templates.map((template) => {
                        const isSelected = selectedTemplates.some(
                          id => String(id).trim() === String(template.id).trim()
                        );
                        return (
                          <label
                            key={template.id}
                            className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleTemplateToggle(template.id)}
                              className="mt-0.5 w-4 h-4 text-[#25D366] border-gray-300 rounded focus:ring-[#25D366]"
                            />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-gray-900">
                                {template.name}
                              </p>
                              {template.category && (
                                <p className="text-[10px] text-gray-500">
                                  {template.category}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {(errors.templateIds || templateValidationError) && (
                    <p className="text-[10px] text-red-600 mt-2">
                      {errors.templateIds || templateValidationError}
                    </p>
                  )}
                  {selectedTemplates.length > 0 && !templateValidationError && (
                    <p className="text-[10px] text-green-600 mt-2">
                      {selectedTemplates.length} template{selectedTemplates.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-120px)]">
              {originalTemplate ? (
                <div>
                  <TemplatePreview
                    state={transformDuplicateTemplateToCreatorState(originalTemplate)}
                  />
                  {selectedTemplates.length > 1 && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-[10px] text-blue-800">
                        Preview shows first template. {selectedTemplates.length} templates will be shuffled when sending.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">
                    Select at least one template to see preview
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCampaignForm;

